import { useInstantSearch } from 'react-instantsearch';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { FilterTitle } from '@sorare/core/src/components/search/FilterTitle';
import { useFacet } from '@sorare/core/src/contexts/graphql-search';
import type { FilterWidget } from '@sorare/core/src/lib/filters';
import { FILTERS } from '@sorare/core/src/lib/filters';
import { filters } from '@sorare/core/src/lib/glossary';

import {
  BackendRangeSlider,
  RangeSlider,
} from '#src/search/RangeSlider/index.tsx';
import { makeRangeFilter } from '#src/searchCards/RangeSlider/index.tsx';

const AverageScoreTitle = () => {
  const { indexUiState } = useInstantSearch();

  const selected =
    (indexUiState.range?.[FILTERS.lastFiveAverageScore.attribute] ? 1 : 0) +
    (indexUiState.range?.[FILTERS.lastFifteenAverageScore.attribute] ? 1 : 0);

  return <FilterTitle name={filters.averageScore} selected={selected} />;
};

const Section = styled(Vertical)`
  &:not(:has(.FilterWidget.visible)) {
    display: none;
  }
`;

const AverageScoreComponent = ({
  fiveAverageScoreWidget,
  fifteenAverageScoreWidget,
  visible,
}: {
  fiveAverageScoreWidget: FilterWidget;
  fifteenAverageScoreWidget: FilterWidget;
  visible: boolean;
}) => {
  return (
    <Vertical gap={3}>
      <Section>
        <LabelM color="var(--c-nd-600)">
          <FormattedMessage {...FILTERS.lastFiveAverageScore.title} />
        </LabelM>
        <fiveAverageScoreWidget.Component
          visible={visible}
          {...fiveAverageScoreWidget.props}
        />
      </Section>
      <Section>
        <LabelM color="var(--c-nd-600)">
          <FormattedMessage {...FILTERS.lastFifteenAverageScore.title} />
        </LabelM>
        <fifteenAverageScoreWidget.Component
          visible={visible}
          {...fifteenAverageScoreWidget.props}
        />
      </Section>
    </Vertical>
  );
};

export const RefineAverageScore = (): FilterWidget => {
  const fiveAverageScoreWidget = makeRangeFilter(
    ({ visible }) => (
      <RangeSlider
        attribute={FILTERS.lastFiveAverageScore.attribute}
        visible={visible}
      />
    ),
    FILTERS.lastFiveAverageScore
  );

  const fifteenAverageScoreWidget = makeRangeFilter(
    ({ visible }) => (
      <RangeSlider
        attribute={FILTERS.lastFifteenAverageScore.attribute}
        visible={visible}
      />
    ),
    FILTERS.lastFifteenAverageScore
  );

  return {
    key: FILTERS.averageScore.attribute,
    type: 'range',
    attribute: FILTERS.averageScore.attribute,
    title: <AverageScoreTitle />,
    Component: ({ visible }) => (
      <AverageScoreComponent
        fiveAverageScoreWidget={fiveAverageScoreWidget}
        fifteenAverageScoreWidget={fifteenAverageScoreWidget}
        visible={visible}
      />
    ),
  };
};

const BackendScoreTitle = () => {
  const { selected: selectedL5 } = useFacet('so5L5Avg');
  const { selected: selectedL15 } = useFacet('so5L15Avg');

  return (
    <FilterTitle
      name={FILTERS.averageScore.title}
      selected={[selectedL5, selectedL15].filter(Boolean).length}
    />
  );
};

export const RefineBackendAverageScore = (): FilterWidget => {
  const fiveAverageScoreWidget = {
    key: 'so5L5Avg',
    type: 'range',
    attribute: 'so5L5Avg',
    Component: ({ visible }) => (
      <BackendRangeSlider visible={visible} attribute="so5L5Avg" />
    ),
  } satisfies FilterWidget;

  const fifteenAverageScoreWidget = {
    key: 'so5L15Avg',
    type: 'range',
    attribute: 'so5L15Avg',
    Component: ({ visible }) => (
      <BackendRangeSlider visible={visible} attribute="so5L15Avg" />
    ),
  } satisfies FilterWidget;

  return {
    key: FILTERS.averageScore.attribute,
    type: 'range',
    attribute: FILTERS.averageScore.attribute,
    title: <BackendScoreTitle />,
    Component: ({ visible }) => (
      <AverageScoreComponent
        fiveAverageScoreWidget={fiveAverageScoreWidget}
        fifteenAverageScoreWidget={fifteenAverageScoreWidget}
        visible={visible}
      />
    ),
  };
};
